/**
 * Returns Mid & Nat price calculated for two options.
 *
 * @param {Object} firstMLOLeg Containing multi-leg-order first leg data
 * @param {Object} secondMLOLeg Containing multi-leg-order second leg data
 *
 * @return {Object} mid, nat price
 */
export function calculateMidNatForOptionOption(firstMLOLeg, secondMLOLeg) {
  if (!secondMLOLeg) {
    return {
      mid: (firstMLOLeg.bid + firstMLOLeg.ask) / 2,
      nat: firstMLOLeg.actionType.isBuy() ? firstMLOLeg.ask : firstMLOLeg.buy
    };
  }

  const multiplier1 = firstMLOLeg.lotSize;
  const multiplier2 = secondMLOLeg.lotSize;
  const quantity1 = firstMLOLeg.quantity * multiplier1;
  const quantity2 = secondMLOLeg.quantity * multiplier2;

  if (quantity1 === 0 || quantity2 === 0) {
    return { mid: 0, nat: 0 };
  }

  const mid1 = (firstMLOLeg.ask + firstMLOLeg.bid) / 2;
  const mid2 = (secondMLOLeg.ask + secondMLOLeg.bid) / 2;

  const firstLegAsk = firstMLOLeg.actionType.isBuy();
  const secondLegAsk = secondMLOLeg.actionType.isBuy();

  const nat1 = firstLegAsk ? firstMLOLeg.ask : firstMLOLeg.bid;
  const nat2 = secondLegAsk ? secondMLOLeg.ask : secondMLOLeg.bid;

  const buySellFactor1 = firstMLOLeg.actionType.isBuy() ? 1 : -1;
  const buySellFactor2 = secondMLOLeg.actionType.isBuy() ? 1 : -1;

  const globalMultiplier = Math.max(multiplier1, multiplier2);

  const grossAmtMid1 = mid1 * quantity1 * buySellFactor1;
  const grossAmtMid2 = mid2 * quantity2 * buySellFactor2;
  const grossAmtNat1 = nat1 * quantity1 * buySellFactor1;
  const grossAmtNat2 = nat2 * quantity2 * buySellFactor2;

  const totalMid = (grossAmtMid1 + grossAmtMid2) / globalMultiplier;
  const totalNat = (grossAmtNat1 + grossAmtNat2) / globalMultiplier;

  const divisor = ggt(firstMLOLeg.quantity, secondMLOLeg.quantity);

  const globalMid = totalMid / divisor;
  const globalNat = totalNat / divisor;

  return {
    mid: parseFloat(globalMid),
    nat: parseFloat(globalNat)
  };
}

/**
 * Returns Mid & Nat price calculated for a stock & an option.
 *
 * @param {Object} stockLeg Containing stock leg data
 * @param {Object} optionLeg Containing option leg data
 *
 * @return {Object} mid, nat price
 */
export function calculateMidNatForStockOption(stockLeg, optionLeg) {
  const quantity1 = stockLeg.quantity;
  let quantity2 = optionLeg.quantity;
  quantity2 *= optionLeg.lotSize;

  let eqvContract1 = 1;
  let eqvContract2 = quantity2 / quantity1;
  if (stockLeg.actionType.isSell()) {
    eqvContract1 = -eqvContract1;
  }
  if (optionLeg.actionType.isSell()) {
    eqvContract2 = -eqvContract2;
  }

  const mid1 = (stockLeg.ask + stockLeg.bid) / 2;
  const mid2 = (optionLeg.ask + optionLeg.bid) / 2;
  const firstLegAsk = stockLeg.actionType.isBuy();
  const secondLegAsk = optionLeg.actionType.isBuy();

  const nat1 = firstLegAsk ? stockLeg.ask : stockLeg.bid;
  const nat2 = secondLegAsk ? optionLeg.ask : optionLeg.bid;

  const globalMidPrice = mid1 * eqvContract1 + mid2 * eqvContract2;
  const globalNatPrice = nat1 * eqvContract1 + nat2 * eqvContract2;

  return {
    mid: parseFloat(globalMidPrice),
    nat: parseFloat(globalNatPrice)
  };
}

/* Helpers */
function ggt(number1, number2) {
  if (Number.isNaN(Number.parseFloat(number1)) || Number.isNaN(Number.parseFloat(number2))) {
    return Number.NaN;
  }
  while (number2 !== 0) {
    if (number1 > number2) {
      number1 = number1 - number2;
    } else {
      number2 = number2 - number1;
    }
  }
  return number1;
}

export default {
  calculateMidNatForOptionOption,
  calculateMidNatForStockOption
};
